// Agent Scorecard - 7-dimension performance radar, activity heatmap, trends
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Users,
  Phone, MessageSquare, Mail, CheckCircle, Clock, Target,
  Award, Activity, BarChart3, Shield, Zap, UserPlus
} from 'lucide-react';
import { TourBubble } from '../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const SCORECARD_TOUR_STEPS = [
  { type: 'splash', title: 'Agent Scorecard', description: 'A comprehensive 7-dimension performance profile for each agent. Weighted scoring, activity heatmaps, trend analysis, and percentile ranking vs the agency average.' },
  { target: 'scorecard-period', emoji: '📅', title: 'Time Period', description: 'Switch between 30, 60, and 90-day views. Short windows show recent momentum; longer windows reveal consistent patterns.', position: 'bottom' },
  { target: 'scorecard-composite', emoji: '🎯', title: 'Composite Score', description: 'The agent\'s overall performance score (0-100), weighted across 7 dimensions. Shows their percentile rank vs the agency average. Green = top performer, red = needs coaching.', position: 'right' },
  { target: 'scorecard-stats', emoji: '📊', title: 'Quick Stats', description: 'Key metrics at a glance — total clients, net growth, communications sent, response time, task completion, satisfaction, retention rate, and active clients.', position: 'bottom' },
  { target: 'scorecard-radar', emoji: '🕸️', title: 'Performance Radar', description: 'Visual representation of all 7 dimensions. A balanced agent fills the entire chart. Dips reveal specific areas for improvement — use this in coaching sessions.', position: 'right' },
  { target: 'scorecard-dimensions', emoji: '📈', title: 'Score Breakdown', description: 'Each dimension with its weight, score, and detail. Retention (30%), Response Time (15%), Follow-up (15%), Satisfaction (10%), Activity (10%), Tasks (10%), Growth (10%).', position: 'left' },
  { target: 'scorecard-heatmap', emoji: '🗓️', title: 'Activity Heatmap', description: 'GitHub-style 90-day activity grid. Darker green = more activity that day. Hover for details — calls, SMS, emails, and tasks. Spot patterns: consistent daily activity beats occasional bursts.', position: 'right' },
  { target: 'scorecard-trends', emoji: '📉', title: 'Trend Comparison', description: 'Compare 30/60/90-day retention rates and daily activity averages. Upward trends indicate improvement; declining trends signal the need for intervention.', position: 'left' },
  { type: 'celebration', title: 'Coach With Data!', description: 'Use scorecards in 1:1 meetings. Agents who receive data-driven coaching improve their composite score by an average of 15 points within 60 days.' },
];

// Color scale for heatmap
const HEATMAP_COLORS = [
  'bg-gray-100',    // 0
  'bg-green-200',   // 1-2
  'bg-green-300',   // 3-5
  'bg-green-400',   // 6-10
  'bg-green-600',   // 11+
];

function getHeatmapColor(count) {
  if (count === 0) return HEATMAP_COLORS[0];
  if (count <= 2) return HEATMAP_COLORS[1];
  if (count <= 5) return HEATMAP_COLORS[2];
  if (count <= 10) return HEATMAP_COLORS[3];
  return HEATMAP_COLORS[4];
}

// Dimension color based on score
function getDimensionColor(score) {
  if (score >= 80) return { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' };
  if (score >= 60) return { bg: 'bg-yellow-100', text: 'text-yellow-700', bar: 'bg-yellow-500' };
  if (score >= 40) return { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-500' };
  return { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' };
}

// Radar chart (pure SVG)
function RadarChart({ dimensions, size = 280 }) {
  const center = size / 2;
  const radius = (size / 2) - 40;
  const angleStep = (Math.PI * 2) / dimensions.length;

  // Grid rings
  const rings = [0.25, 0.5, 0.75, 1.0];

  // Calculate points for polygon
  const points = dimensions.map((dim, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const r = (dim.score / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });

  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[300px] mx-auto">
      {/* Grid rings */}
      {rings.map((ring) => (
        <polygon
          key={ring}
          points={dimensions.map((_, i) => {
            const angle = angleStep * i - Math.PI / 2;
            const r = ring * radius;
            return `${center + r * Math.cos(angle)},${center + r * Math.sin(angle)}`;
          }).join(' ')}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}

      {/* Axis lines */}
      {dimensions.map((_, i) => {
        const angle = angleStep * i - Math.PI / 2;
        return (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={center + radius * Math.cos(angle)}
            y2={center + radius * Math.sin(angle)}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        );
      })}

      {/* Data polygon */}
      <polygon
        points={polygonPoints}
        fill="rgba(255, 184, 0, 0.2)"
        stroke="#FFB800"
        strokeWidth="2"
      />

      {/* Data points */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="4"
          fill="#FFB800"
          stroke="white"
          strokeWidth="2"
        />
      ))}

      {/* Labels */}
      {dimensions.map((dim, i) => {
        const angle = angleStep * i - Math.PI / 2;
        const labelR = radius + 25;
        const x = center + labelR * Math.cos(angle);
        const y = center + labelR * Math.sin(angle);
        const abbrev = dim.label.split(' ').map(w => w[0]).join('');
        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-gray-500 text-[10px] font-medium"
          >
            {abbrev}
          </text>
        );
      })}
    </svg>
  );
}

// Activity Heatmap (GitHub-style)
function ActivityHeatmap({ heatmap }) {
  if (!heatmap || heatmap.length === 0) return null;

  // Group by week (columns) with 7 rows (Mon-Sun)
  const weeks = [];
  let currentWeek = [];
  const firstDate = new Date(heatmap[0].date);
  const startDow = firstDate.getDay(); // 0=Sun

  // Pad the first week
  for (let i = 0; i < startDow; i++) {
    currentWeek.push(null);
  }

  for (const day of heatmap) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-[3px]">
        {/* Day labels */}
        <div className="flex flex-col gap-[3px] mr-1 text-[10px] text-gray-400">
          <span className="h-[14px]">S</span>
          <span className="h-[14px]">M</span>
          <span className="h-[14px]">T</span>
          <span className="h-[14px]">W</span>
          <span className="h-[14px]">T</span>
          <span className="h-[14px]">F</span>
          <span className="h-[14px]">S</span>
        </div>

        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            {week.map((day, di) => (
              <div
                key={di}
                className={`w-[14px] h-[14px] rounded-sm ${day ? getHeatmapColor(day.total) : 'bg-transparent'}`}
                title={day ? `${day.date}: ${day.total} activities (${day.calls} calls, ${day.sms} SMS, ${day.emails} emails, ${day.tasks} tasks)` : ''}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-1 text-[10px] text-gray-400">
        <span>Less</span>
        {HEATMAP_COLORS.map((color, i) => (
          <div key={i} className={`w-[12px] h-[12px] rounded-sm ${color}`} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

// Score circle
function ScoreCircle({ score, size = 120, label }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : score >= 40 ? '#f97316' : '#ef4444';

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-3xl font-bold text-gray-900">{score}</span>
        {label && <span className="text-xs text-gray-500">{label}</span>}
      </div>
    </div>
  );
}

// Metric card
function MetricCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-500">{title}</span>
        {Icon && <Icon size={16} className="text-gray-400" />}
      </div>
      <div className="text-2xl font-bold" style={{ color: color || '#172A3A' }}>{value}</div>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

export default function AgentScorecard() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(90);

  // Tour state
  const [scTourStep, setScTourStep] = useState(0);
  const [showScTour, setShowScTour] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('agent-scorecard-tour-dismissed')) setShowScTour(true);
  }, []);

  const advanceScTour = useCallback(() => {
    setScTourStep(s => {
      if (s + 1 >= SCORECARD_TOUR_STEPS.length) {
        localStorage.setItem('agent-scorecard-tour-dismissed', 'true');
        setShowScTour(false);
        return 0;
      }
      return s + 1;
    });
  }, []);
  const backScTour = useCallback(() => setScTourStep(s => Math.max(0, s - 1)), []);
  const dismissScTour = useCallback(() => { localStorage.setItem('agent-scorecard-tour-dismissed', 'true'); setShowScTour(false); }, []);

  useEffect(() => {
    if (!showScTour) return;
    const h = (e) => {
      if (e.key === 'Escape') dismissScTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') advanceScTour();
      if (e.key === 'ArrowLeft') backScTour();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [showScTour, advanceScTour, backScTour, dismissScTour]);

  const fetchScorecard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/agency/scorecard/${agentId}?period=${period}`, {
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (err) {
      console.error('[AgentScorecard] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [agentId, period]);

  useEffect(() => {
    if (agentId) fetchScorecard();
  }, [agentId, fetchScorecard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading scorecard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft size={20} /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700 font-medium">{error}</p>
          <button onClick={fetchScorecard} className="mt-3 text-sm text-red-600 underline">Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { agent, dimensions, compositeScore, percentileRank, agencyAvgRetention, stats, heatmap, trends } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/agency/agents')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-[#172A3A]">Agent Scorecard</h1>
                <p className="text-sm text-gray-500">{agent.name} &middot; {agent.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Period toggle */}
              <div className="flex bg-gray-100 rounded-lg p-0.5" data-tour="scorecard-period">
                {[30, 60, 90].map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      period === p ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {p}d
                  </button>
                ))}
              </div>
              <button
                onClick={fetchScorecard}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw size={18} className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Top Row: Composite Score + Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Composite Score Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center" data-tour="scorecard-composite">
            <h3 className="text-sm font-medium text-gray-500 mb-4">Composite Score</h3>
            <div className="relative inline-flex items-center justify-center">
              <ScoreCircle score={compositeScore} />
            </div>
            <div className="mt-4 flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-400">Percentile</p>
                <p className="text-lg font-bold text-[#172A3A]">{percentileRank}th</p>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="text-center">
                <p className="text-xs text-gray-400">Agency Avg</p>
                <p className="text-lg font-bold text-gray-500">{agencyAvgRetention}%</p>
              </div>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3" data-tour="scorecard-stats">
            <MetricCard title="Total Clients" value={stats.totalClients} icon={Users} />
            <MetricCard
              title="Net Growth"
              value={stats.netGrowth >= 0 ? `+${stats.netGrowth}` : stats.netGrowth}
              icon={stats.netGrowth >= 0 ? TrendingUp : TrendingDown}
              color={stats.netGrowth >= 0 ? '#22c55e' : '#ef4444'}
              subtitle={`+${stats.newClients} new, -${stats.lostClients} lost`}
            />
            <MetricCard
              title="Total Comms"
              value={stats.totalComms}
              icon={MessageSquare}
              subtitle={`${period}d period`}
            />
            <MetricCard
              title="Avg Response"
              value={stats.avgResponseHours > 0 ? `${stats.avgResponseHours}h` : 'N/A'}
              icon={Clock}
              subtitle={stats.totalInbound > 0 ? `${stats.respondedCount}/${stats.totalInbound} replied` : 'No inbound'}
            />
            <MetricCard
              title="Tasks Done"
              value={`${stats.completedTasks}/${stats.totalTasks}`}
              icon={CheckCircle}
              subtitle={stats.onTimeTasks > 0 ? `${stats.onTimeTasks} on time` : ''}
            />
            <MetricCard
              title="Satisfaction"
              value={stats.satisfactionResponses > 0 ? `${stats.avgSatisfaction}/5` : 'N/A'}
              icon={Award}
              subtitle={`${stats.satisfactionResponses} responses`}
            />
            <MetricCard
              title="Retention"
              value={`${dimensions.find(d => d.key === 'retention')?.score || 0}%`}
              icon={Shield}
              color={dimensions.find(d => d.key === 'retention')?.score >= 90 ? '#22c55e' : '#f97316'}
            />
            <MetricCard
              title="Active Clients"
              value={stats.activeClients}
              icon={Activity}
            />
          </div>
        </div>

        {/* Radar Chart + Dimension Bars */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Radar Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="scorecard-radar">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Performance Radar</h3>
            <RadarChart dimensions={dimensions} />
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              {dimensions.map(dim => (
                <div key={dim.key} className="flex items-center gap-2">
                  <span className="font-medium text-gray-600">
                    {dim.label.split(' ').map(w => w[0]).join('')}
                  </span>
                  <span className="text-gray-400">= {dim.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dimension Bars */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="scorecard-dimensions">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Score Breakdown</h3>
            <div className="space-y-4">
              {dimensions.map(dim => {
                const colors = getDimensionColor(dim.score);
                return (
                  <div key={dim.key}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">{dim.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                          {dim.weight}%
                        </span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{dim.score}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${colors.bar}`}
                        style={{ width: `${dim.score}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{dim.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Activity Heatmap + Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Heatmap */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="scorecard-heatmap">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Activity Heatmap (90 days)</h3>
            <ActivityHeatmap heatmap={heatmap} />
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-blue-600">
                  {(heatmap || []).reduce((s, d) => s + d.calls, 0)}
                </p>
                <p className="text-xs text-gray-400">Calls</p>
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">
                  {(heatmap || []).reduce((s, d) => s + d.sms, 0)}
                </p>
                <p className="text-xs text-gray-400">SMS</p>
              </div>
              <div>
                <p className="text-lg font-bold text-red-600">
                  {(heatmap || []).reduce((s, d) => s + d.emails, 0)}
                </p>
                <p className="text-xs text-gray-400">Emails</p>
              </div>
              <div>
                <p className="text-lg font-bold text-purple-600">
                  {(heatmap || []).reduce((s, d) => s + d.tasks, 0)}
                </p>
                <p className="text-xs text-gray-400">Tasks</p>
              </div>
            </div>
          </div>

          {/* Trend Comparison */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="scorecard-trends">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Trend Comparison</h3>
            <div className="space-y-6">
              {/* Retention trend */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Retention Rate</p>
                <div className="flex items-end gap-6">
                  {['30d', '60d', '90d'].map(key => (
                    <div key={key} className="text-center flex-1">
                      <div className="h-16 flex items-end justify-center mb-1">
                        <div
                          className="w-12 bg-amber-400 rounded-t-lg transition-all"
                          style={{ height: `${(trends[key]?.retention || 0) * 0.64}px` }}
                        />
                      </div>
                      <p className="text-lg font-bold text-gray-900">{trends[key]?.retention || 0}%</p>
                      <p className="text-xs text-gray-400">{key}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Activity trend */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Daily Activity Avg</p>
                <div className="flex items-end gap-6">
                  {['30d', '60d', '90d'].map(key => (
                    <div key={key} className="text-center flex-1">
                      <div className="h-16 flex items-end justify-center mb-1">
                        <div
                          className="w-12 bg-blue-400 rounded-t-lg transition-all"
                          style={{ height: `${Math.min(64, (trends[key]?.dailyAvg || 0) * 12)}px` }}
                        />
                      </div>
                      <p className="text-lg font-bold text-gray-900">{trends[key]?.dailyAvg || 0}</p>
                      <p className="text-xs text-gray-400">{key}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showScTour && (
        <TourBubble
          stepData={SCORECARD_TOUR_STEPS[scTourStep]}
          stepIndex={scTourStep}
          totalSteps={SCORECARD_TOUR_STEPS.length}
          onNext={advanceScTour}
          onBack={backScTour}
          onDismiss={dismissScTour}
          splashIcon={<Award className="w-10 h-10 text-amber-500" />}
          maskId="agent-scorecard-tour-mask"
        />
      )}
    </div>
  );
}
